// AutoClockoutBanner — the cyan notice on the Live In Club screen that warns a
// Manager when the scheduled auto-clear (#10, ADR 0010) will run: "Auto
// clock-out at 1:00 AM · in 2h 15m", with a live countdown.
//
// Presentational and pure: the page owns the hooks (useFeatureFlag, the settings
// useQuery, useNow) and passes their values in as props, so this component is
// unit-testable with a fixed `now` and needs no router/query context. It renders
// nothing unless ALL of: the component has mounted (SSR-safe — the countdown is
// zone/now-sensitive), the auto-clockout flag is on, the Club has a configured
// close time, and at least one Talent is clocked in — so the UI never advertises
// an auto-clockout the cron would not actually perform.
//
// The close time is reused from the same zoned math the cron uses
// (`nextCloseAtMs`), so the banner and the cron always agree on when close is.

import { classlist } from '~/_shared/design-system/lib/classlist';
import { nextCloseAtMs } from '~/_shared/scheduled/should-reset-at';

import './auto-clockout-banner.css';

interface AutoClockoutSettings {
	timezone: string;
	autoClockoutAt: string | null;
}

interface AutoClockoutBannerProps {
	id?: string;
	className?: string;
	/** The `auto-clockout` feature flag, read by the page via useFeatureFlag. */
	enabled: boolean;
	/** False during SSR/first paint; gates the zone/now-sensitive countdown. */
	isMounted: boolean;
	/** The Club's settings; null/undefined when unloaded or not configured. */
	settings?: AutoClockoutSettings | null;
	/** Talent currently clocked in — the banner is hidden when zero. */
	inClubCount: number;
	/** The ticking clock, from useNow. */
	now: Date;
}

// "01:00:00" / "01:00" → "1:00 AM". The close time is a wall-clock local
// time-of-day, so this is timezone-independent formatting.
function formatCloseLabel(closeAt: string): string {
	const [hStr, mStr] = closeAt.split(':');
	const h = Number(hStr);
	const m = Number(mStr);
	const period = h < 12 ? 'AM' : 'PM';
	const h12 = h % 12 === 0 ? 12 : h % 12;
	return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// Milliseconds until close → "Nh Mm". `nextCloseAtMs` guarantees a value at or
// after `now`, so the difference is never negative.
function formatRemaining(ms: number): string {
	const totalMinutes = Math.floor(ms / 60_000);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return `${hours}h ${minutes}m`;
}

const AutoClockoutBanner = ({
	id = 'AutoClockoutBanner',
	className,
	enabled,
	isMounted,
	settings,
	inClubCount,
	now,
}: AutoClockoutBannerProps) => {
	// AND gate (ADR 0010): mounted + flag on + at least one Talent in + a
	// configured close time. Any miss → render nothing.
	if (!isMounted || !enabled || inClubCount < 1) return null;
	if (!settings?.autoClockoutAt) return null;

	const closeAt = settings.autoClockoutAt;
	const nextMs = nextCloseAtMs(settings.timezone, closeAt, now);
	if (nextMs === null) return null;

	return (
		<div id={id} className={classlist('auto-clockout-banner', className)}>
			<span aria-hidden='true' className='auto-clockout-banner__dot' />
			<p className='auto-clockout-banner__text'>
				Auto clock-out at{' '}
				<span className='auto-clockout-banner__time'>
					{formatCloseLabel(closeAt)}
				</span>
				<span className='auto-clockout-banner__sep'> · </span>in{' '}
				<span className='auto-clockout-banner__countdown'>
					{formatRemaining(nextMs - now.getTime())}
				</span>
			</p>
		</div>
	);
};

export type { AutoClockoutBannerProps };
export { AutoClockoutBanner };
